import { z } from 'zod'

// Common field schemas
const titleSchema = z.string()
  .min(3, 'Title must be at least 3 characters')
  .max(100, 'Title must be less than 100 characters')

const descriptionSchema = z.string()
  .min(20, 'Description must be at least 20 characters')
  .max(5000, 'Description must be less than 5000 characters')

// Base listing schema
export const baseListingSchema = z.object({
  title: titleSchema,
  description: descriptionSchema,
  price: z.preprocess(
    (val) => (val === '' || val === null || val === undefined || Number.isNaN(val) ? undefined : Number(val)),
    z.number({ required_error: 'Price is required', invalid_type_error: 'Please enter a valid price' })
      .min(0, 'Price must be positive')
      .max(100000000, 'Price exceeds maximum')
      .optional(),
  ),
  category: z.string().min(1, 'Please select a category'),
  listingType: z.enum(['general', 'vehicle', 'fundraiser'] as const),
  saleType: z.enum(['buynow', 'auction', 'product'] as const).optional(),
  condition: z.preprocess((v) => (v === '' ? undefined : v), z.enum(['new', 'excellent', 'good', 'fair', 'used'] as const).optional()),
  location: z.string().min(1, 'Please select a region'),
  suburb: z.string().optional(),
  // Auction form fields (only used when saleType === 'auction')
  auctionDuration: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(1).max(30).optional()),
  reservePrice: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(0).optional()),
  buyNowPrice: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(0).optional()),
  // Product form fields (only used when saleType === 'product')
  stock: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(0).max(1000000).optional()),
  sku: z.string().optional(),
})

// Vehicle-specific schema
export const vehicleSchema = baseListingSchema.extend({
  listingType: z.literal('vehicle'),
  make: z.string().min(1, 'Make is required'),
  model: z.string().min(1, 'Model is required'),
  year: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number({ required_error: 'Year is required' }).min(1900, 'Year must be 1900 or later').max(new Date().getFullYear() + 1, 'Year cannot be in the future')),
  odometer: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number({ required_error: 'Odometer is required' }).min(0, 'Odometer must be positive')),
  colour: z.string().min(1, 'Colour is required'),
  bodyType: z.string().optional(),
  doors: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(1).max(10).optional()),
  engineCC: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(0).optional()),
  fuelType: z.preprocess((v) => (v === '' ? undefined : v), z.enum(['Petrol', 'Diesel', 'Electric', 'Hybrid', 'LPG'] as const).optional()),
  transmission: z.preprocess((v) => (v === '' ? undefined : v), z.enum(['Automatic', 'Manual'] as const).optional()),
  previousOwners: z.string().optional(),
  wofExpiry: z.string().optional(),
  regoExpiry: z.string().optional(),
})

// Auction-specific fields
export const auctionSchema = z.object({
  saleType: z.literal('auction'),
  startingPrice: z.number().min(0, 'Starting price must be positive'),
  reservePrice: z.number().min(0).optional(),
  duration: z.number().min(1).max(30, 'Duration must be between 1-30 days'),
})

// General listing schema
export const generalListingSchema = baseListingSchema.extend({
  listingType: z.literal('general'),
})

// Fundraiser product schema
export const fundraiserProductSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Product name is required'),
  price: z.number().min(0, 'Price must be positive'),
  description: z.string().optional(),
  image: z.string().optional(),
  stock: z.number().min(0).optional(),
})

// Raffle prize schema
export const rafflePrizeSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Prize name is required'),
  description: z.string().optional(),
  value: z.number().min(0).optional(),
  image: z.string().optional(),
})

// Sponsorship tier schema
export const sponsorshipTierSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Tier name is required'),
  amount: z.number().min(0, 'Amount must be positive'),
  description: z.string().min(1, 'Description is required'),
  benefits: z.array(z.string()).default([]),
  limit: z.number().min(0).optional(),
})

// Number fields registered via `valueAsNumber: true` pass `null` for empty
// inputs — wrap with preprocess so Zod treats empty/null/NaN as "not provided".
const optionalNumber = (min = 0) =>
  z.preprocess(
    (v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)),
    z.number().min(min).optional(),
  )

// Fundraiser-specific schema
export const fundraiserSchema = baseListingSchema.extend({
  listingType: z.literal('fundraiser'),
  fundraiserType: z.enum(['auction', 'products', 'raffle', 'sponsorship'] as const),
  organizationName: z.string().min(1, 'Organization name is required'),
  closingDate: z.string().optional(),
  targetAmount: optionalNumber(0),
  // Auction-specific
  bidIncrement: optionalNumber(0),
  deliveryOptions: z.array(z.string()).optional(),
  // Products
  products: z.array(fundraiserProductSchema).optional(),
  // Raffle
  ticketPrice: optionalNumber(0),
  totalTickets: optionalNumber(1),
  maxTicketsPerPerson: optionalNumber(1),
  prizes: z.array(rafflePrizeSchema).optional(),
  termsAndConditions: z.string().max(5000).optional(),
  // Sponsorship
  tiers: z.array(sponsorshipTierSchema).optional(),
  sponsorRecognition: z.string().max(2000).optional(),
})

// Store slug schema (URL-safe identifier for /store/slug routes)
export const storeSlugSchema = z.string()
  .min(3, 'Store URL must be at least 3 characters')
  .max(40, 'Store URL must be less than 40 characters')
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Only lowercase letters, numbers, and hyphens (no leading/trailing hyphens)')
  .refine(s => !s.includes('--'), 'No consecutive hyphens allowed')

// Profile schema
export const profileSchema = z.object({
  displayName: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters'),
  bio: z.string().max(500, 'Bio must be less than 500 characters').optional(),
  location: z.string().optional(),
  phone: z.string()
    .regex(/^(\+64|0)?[2-9]\d{7,9}$/, 'Please enter a valid NZ phone number')
    .optional()
    .or(z.literal('')),
  showEmail: z.boolean().optional(),
  showPhone: z.boolean().optional(),
  showEmailOnListings: z.boolean().optional(),
  enableChat: z.boolean().optional(),
  enableEnquiryForm: z.boolean().optional(),
  storeSlug: storeSlugSchema.optional().or(z.literal('')),
})

// Strong password schema (for signup and password change)
export const strongPasswordSchema = z.string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Must contain at least one number')

// Login schema
export const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})

// Signup schema
export const signupSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: strongPasswordSchema,
  confirmPassword: z.string(),
  displayName: z.string().min(2, 'Name must be at least 2 characters'),
  acceptTerms: z.boolean().refine(val => val === true, 'You must accept the terms'),
}).refine(data => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
})

// Change password schema
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: strongPasswordSchema,
  confirmNewPassword: z.string(),
}).refine(data => data.newPassword === data.confirmNewPassword, {
  message: 'Passwords do not match',
  path: ['confirmNewPassword'],
})

// Bug report schema
export const bugReportSchema = z.object({
  title: z.string().min(5, 'Title must be at least 5 characters').max(200, 'Title must be less than 200 characters'),
  description: z.string().min(20, 'Please provide more detail (at least 20 characters)').max(5000),
  category: z.enum(['bug', 'feature', 'feedback', 'other'] as const),
  priority: z.enum(['low', 'medium', 'high', 'critical'] as const),
})

// Offer schema
export const offerSchema = z.object({
  amount: z.number().min(1, 'Offer must be at least $1'),
  message: z.string().max(500, 'Message must be less than 500 characters').optional(),
})

// Review schema
export const reviewSchema = z.object({
  rating: z.number().min(1, 'Please select a rating').max(5),
  comment: z.string().min(10, 'Review must be at least 10 characters').max(2000),
})

// Type exports
export type BaseListingFormData = z.infer<typeof baseListingSchema>
export type VehicleFormData = z.infer<typeof vehicleSchema>
export type GeneralFormData = z.infer<typeof generalListingSchema>
export type FundraiserFormData = z.infer<typeof fundraiserSchema>
export type ProfileFormData = z.infer<typeof profileSchema>
export type LoginFormData = z.infer<typeof loginSchema>
export type SignupFormData = z.infer<typeof signupSchema>
